/* eslint-disable camelcase */
import { useCallback, useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ProgressButtons, useNotificationContext } from "cerulean";
import { v4 as uuidv4 } from "uuid";
import { ResponsiveFlex } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { entities, modules, cents, useFeature } from "byzantine";
import filters from "byzantine/src/filters";
import Account from "byzantine/src/Account";
import {
  getIsOneTimePaymentForToday,
  useMappedEntryDescription,
} from "byzantine/src/dbbl/businessLogic/modules/achPayments";
import { useIsACHDualApprovalRequired } from "byzantine/src/dbbl/businessLogic/entities/users/hooks";
import { useFingerPrintData } from "../../fingerprint";
import { useTriggerGuideForceReset } from "../../../src/modules/guides/hooks";
import HeaderWithSteps from "../../HeaderWithSteps";
import AccountContext from "../../contexts/AccountContext";
import { useInstitutionSettings } from "../../contexts/InstitutionSettingsContext";
import { RowWrapper } from "../../PaymentActivity/CreateBulk/ReviewBulkScreen";
import { formatFrequencyValue } from "../FrequencySelector";
import styles from "./ReviewScreen.module.scss";
import {
  ACH_PAYMENT_ROUTE,
  ACH_PAYMENT_ADDITIONAL_DETAILS_ROUTE,
  ACH_PAYMENT_RECEIPT_ROUTE,
  ACH_PAYMENT_DETAIL_ROUTE,
} from "./ACHPaymentRoutes";

type RowType = [string?, string?];

const ReviewScreen = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { accounts } = useContext(AccountContext);
  const { sendNotification } = useNotificationContext();
  const { featureEquals } = useFeature();
  const institution = useInstitutionSettings();
  const { createACHPayment, loading: achLoading } =
    modules.achPayments.useCreateACHPayment();
  const { createFedNowPayment, loading: fedNowLoading } =
    modules.achPayments.useCreateFedNowPayment();
  const { send: fetchACHCompanies } =
    entities.achCompanies.useFetchACHCompanies();
  const form = modules.achPayments.ACHPaymentForm.useForm();
  const isDebitPayment = modules.achPayments.useIsDebitACHPayment();
  const selectedRecipient = entities.recipients.useSelectedRecipient();
  const achCompany = entities.achCompanies.useACHCompany();
  const achDualApprovalRequired = useIsACHDualApprovalRequired();
  const { getSelectedEntryDescription } = useMappedEntryDescription();
  const { calculateTotalPayments } =
    modules.achPayments.useCalculateTotalPayments();
  const { captureData } = useFingerPrintData("ach_payment");
  const triggerGuideForceReset = useTriggerGuideForceReset();
  const loading = achLoading || fedNowLoading;

  const { values } = form;
  const {
    amount,
    fromAccount,
    secCode,
    recurringRule,
    memo,
    startDate,
    entryDesc,
    description,
  } = values;
  const isSingleExpediteEnabled =
    modules.achPayments.useIsSingleExpediteEnabled();
  const isInstantDeliverySelected =
    modules.achPayments.useIsInstantACHDelivery();
  const isInstantDelivery =
    isSingleExpediteEnabled && isInstantDeliverySelected;
  const { name, ach_destination, nickname } = selectedRecipient!;
  const {
    account_number: accountNumber,
    routing_number: routingNumber,
    institution_name: institutionName,
  } = ach_destination!;
  const senderAccountData = accounts.find(
    (account: Account) => account.id === fromAccount
  );
  const totalPayments = calculateTotalPayments();
  const totalAmount = (totalPayments * amount) as Cents;
  const isOneTimePaymentForToday = getIsOneTimePaymentForToday(
    values,
    institution.ach_next_cutoff
  );
  const selectedEntryDescription = getSelectedEntryDescription(entryDesc);
  const isSameDayDeliverySelected =
    modules.achPayments.useIsSameDayACHDelivery();
  const isSameDayDelivery =
    isSingleExpediteEnabled && isSameDayDeliverySelected;

  const getReviewSubtitle = () => {
    if (!isDebitPayment && isInstantDelivery) {
      return l10n.getString(
        "single-ach-payment-review-sub-title-credit-instant"
      );
    }
    if (isSameDayDelivery) {
      return isDebitPayment
        ? l10n.getString("single-ach-payment-review-sub-title-debit-same-day")
        : l10n.getString("single-ach-payment-review-sub-title-credit-same-day");
    }
    return isDebitPayment
      ? l10n.getString("single-ach-payment-review-sub-title-debit")
      : l10n.getString("single-ach-payment-review-sub-title-credit");
  };
  const reviewSubtitle = getReviewSubtitle();
  const accountDirectionLabel = isDebitPayment
    ? l10n.getString("single-ach-payment-to-account")
    : l10n.getString("single-ach-payment-from-account");
  const totalPaymentsLabel = isDebitPayment
    ? l10n.getString("single-ach-payment-review-total-payments-debit", {
        totalPayments,
      })
    : l10n.getString("single-ach-payment-review-total-payments-credit", {
        totalPayments,
      });
  const reviewDateLabel = isDebitPayment
    ? l10n.getString("single-ach-payment-review-date-debit")
    : l10n.getString("ach-payment-review-date");
  const nextButtonLabel = isDebitPayment
    ? l10n.getString("ach-payment-collect-review-action")
    : l10n.getString("ach-payment-review-action");

  useEffect(() => {
    fetchACHCompanies(`institution_account=${senderAccountData?.id}`);
  }, [senderAccountData, fetchACHCompanies]);

  const handleGoBack = useCallback(() => {
    // instant delivery skips the additional details step, so "back" from review
    // returns to the payment step instead
    const previousRoute = isInstantDelivery
      ? ACH_PAYMENT_DETAIL_ROUTE
      : ACH_PAYMENT_ADDITIONAL_DETAILS_ROUTE;
    navigate(`/transfer/${ACH_PAYMENT_ROUTE}/${previousRoute}`);
  }, [navigate, isInstantDelivery]);

  const handleError = (screenWithError: {
    screen: number;
    message: string;
  }) => {
    const { screen, message } = screenWithError;

    if (screen === 3) {
      handleGoBack();
    } else if (screen === 2) {
      navigate(`../${ACH_PAYMENT_DETAIL_ROUTE}`);
    } else if (screen === 1) {
      navigate("..");
    }

    sendNotification({
      type: "negative",
      text: message,
    });
  };

  const handleFedNowError = ({ message }: { message: string }) => {
    sendNotification({
      type: "negative",
      text: message,
    });
  };

  const idempotencyKey = useMemo(() => uuidv4() as UUID, []);

  const handleClickSubmit = async () => {
    let deviceIds: { deviceId: string; deviceRequestId: string } | undefined;
    if (
      featureEquals("third_party_ach_transaction_evaluations", [
        "report",
        "decision",
      ])
    ) {
      deviceIds = await captureData();
    }
    const handleACHPaymentSuccess = (
      data: API.ACHPayment | API.ScheduledACHPayment
    ) => {
      triggerGuideForceReset();
      navigate(
        `/transfer/${ACH_PAYMENT_ROUTE}/${ACH_PAYMENT_RECEIPT_ROUTE}/${data.id}`
      );
    };

    const handleFedNowSuccess = (data: API.FedNowTransaction) => {
      triggerGuideForceReset();
      navigate(
        `/transfer/${ACH_PAYMENT_ROUTE}/${ACH_PAYMENT_RECEIPT_ROUTE}/${data.id}`
      );
    };

    if (isInstantDelivery) {
      createFedNowPayment(idempotencyKey, {
        onSuccess: handleFedNowSuccess,
        onError: handleFedNowError,
        deviceIds,
      });
      return;
    }

    createACHPayment(idempotencyKey, {
      onSuccess: handleACHPaymentSuccess,
      onError: handleError,
      deviceIds,
    });
  };

  const getDisplaySendDate = () => {
    if (isOneTimePaymentForToday) {
      return achDualApprovalRequired
        ? l10n.getString("pending-table-one-time-payment")
        : startDate;
    }

    return startDate;
  };

  const renderRow = (row: RowType, index: number) => {
    const [heading, content] = row;

    return (
      <RowWrapper
        key={`row-${heading}__${index}`}
        heading={heading}
        content={content}
      />
    );
  };

  const recipientRows: RowType[] = [
    [l10n.getString("ach-payment-recipient-title"), name],
    [l10n.getString("ach-payment-review-bank"), institutionName],
    [l10n.getString("ach-payment-review-account"), accountNumber],
    [l10n.getString("ach-payment-review-routing"), routingNumber],
  ];

  const internalDetailsRows: RowType[] = [[l10n.getString("label-note"), memo]];

  if (!senderAccountData) {
    return null;
  }

  const senderAccount = new Account(senderAccountData);

  return (
    <ResponsiveFlex gapSize="xl">
      <HeaderWithSteps
        headerText={l10n.getString(
          "single-ach-payment-review-and-confirm-title"
        )}
      />
      <div className={styles.reviewCardContainer}>
        <div className={styles.reviewCardHeader}>
          <p>
            {reviewSubtitle} {name}{" "}
            {nickname ? (
              <span className="fontColor--secondary fontSize--xs">{`(${nickname})`}</span>
            ) : null}
          </p>
          <h1>{`${filters.currency(cents(amount))}`}</h1>
        </div>

        {recurringRule?.frequency && recurringRule?.isVisible ? (
          <div className={styles.reviewCardSectionBordered}>
            <ResponsiveFlex gapSize="m">
              <h5 className="fontSize--xs fontWeight--bold">
                {" "}
                {l10n.getString("ach-payment-payment-schedule").toUpperCase()}
              </h5>
              {startDate ? (
                <RowWrapper
                  heading={l10n.getString("ach-payment-starts")}
                  content={startDate}
                />
              ) : null}
              {recurringRule?.frequency ? (
                <RowWrapper
                  heading={l10n.getString("label-frequency")}
                  content={formatFrequencyValue(recurringRule?.frequency, l10n)}
                />
              ) : null}
              {recurringRule?.ends === "on" && recurringRule?.untilDate ? (
                <RowWrapper
                  heading={l10n.getString("travel-notice-end-date-input")}
                  content={recurringRule?.untilDate}
                />
              ) : null}
              {recurringRule?.ends === "on" ||
              recurringRule?.ends === "after" ? (
                <RowWrapper
                  heading={totalPaymentsLabel}
                  content={filters.currency(totalAmount)}
                />
              ) : null}
            </ResponsiveFlex>
          </div>
        ) : null}

        <div className={styles.reviewCardSectionBordered}>
          <ResponsiveFlex gapSize="m">
            <h5 className="fontSize--xs fontWeight--bold">
              {" "}
              {l10n.getString("ach-payment-payment-details").toUpperCase()}
            </h5>
            <RowWrapper
              heading={accountDirectionLabel}
              content={senderAccount.getShortDescription()}
            />
            {achCompany?.company_id ? (
              <RowWrapper
                heading={l10n.getString("ach-payment-review-company")}
                content={`${achCompany?.company_name} / ${achCompany?.company_id}`}
              />
            ) : null}
            {description ? (
              <RowWrapper
                heading={l10n.getString("label-memo")}
                content={description}
              />
            ) : null}
            <RowWrapper
              heading={l10n.getString("ach-payment-transaction-title")}
              content={`${secCode}`}
            />
            {entryDesc ? (
              <RowWrapper
                heading={l10n.getString("label-entry-description")}
                content={selectedEntryDescription}
              />
            ) : null}
            {!recurringRule?.isVisible ? (
              <RowWrapper
                heading={reviewDateLabel}
                content={getDisplaySendDate()}
              />
            ) : null}
          </ResponsiveFlex>
        </div>

        <div className={styles.reviewCardSectionBordered}>
          <ResponsiveFlex gapSize="m">
            <h5 className="fontSize--xs fontWeight--bold">
              {" "}
              {l10n.getString("ach-payment-recipient-details").toUpperCase()}
            </h5>
            {recipientRows.map((row, index) => renderRow(row, index))}
          </ResponsiveFlex>
        </div>

        {memo ? (
          <div className={styles.reviewCardSectionBordered}>
            <ResponsiveFlex gapSize="m">
              <h5 className="fontSize--xs fontWeight--bold">
                {" "}
                {l10n.getString("ach-payment-internal-details").toUpperCase()}
              </h5>
              {internalDetailsRows.map((row, index) => renderRow(row, index))}
            </ResponsiveFlex>
          </div>
        ) : null}
      </div>
      <ProgressButtons
        backLabel={l10n.getString("button-back")}
        nextLabel={nextButtonLabel}
        onBack={handleGoBack}
        onNext={handleClickSubmit}
        disabled={loading}
        disableNext={loading}
        isNextLoading={loading}
      />
    </ResponsiveFlex>
  );
};

export default ReviewScreen;
